/**
 * Page « Connecteurs IA » (MCP-P1.4) — gestion des autorisations MCP.
 *
 * Route : /workspaces/:id/connectors
 *
 * Permet au créateur de :
 *  - Voir la liste des autorisations MCP actives (Claude Desktop, claude.ai...).
 *  - Voir les scopes accordés à chacune et la dernière utilisation (jour seul).
 *  - Révoquer une autorisation en 1 clic.
 *
 * Côté backend : GET / POST /oauth/authorizations(/:id/revoke).
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getErrorMessage,
  listMcpAuthorizations,
  revokeMcpAuthorization,
  type McpAuthorization,
} from '@/lib/api';

const SCOPE_BADGE: Record<string, { label: string; tone: 'neutral' | 'warning' | 'danger' }> = {
  'surveys:read': { label: 'Lire enquêtes', tone: 'neutral' },
  'surveys:write': { label: 'Modifier enquêtes', tone: 'neutral' },
  'definitions:write': { label: 'Modifier structure', tone: 'neutral' },
  'aggregates:read': { label: 'Stats agrégées', tone: 'neutral' },
  'responses:read': { label: 'Réponses individuelles', tone: 'danger' },
};

export default function ConnectorsPage(): JSX.Element {
  const { id: workspaceId } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['mcp-authorizations', workspaceId],
    queryFn: () => listMcpAuthorizations(workspaceId!),
    enabled: !!workspaceId,
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeMcpAuthorization(workspaceId!, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mcp-authorizations', workspaceId] });
      setConfirmRevoke(null);
    },
  });

  if (!workspaceId) {
    return <p className="text-text-muted">Workspace inconnu.</p>;
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <p className="text-xs uppercase tracking-[0.14em] text-text-muted font-semibold">
          Compte
        </p>
        <h1 className="text-2xl font-semibold">Connecteurs IA</h1>
        <p className="text-text-muted max-w-3xl">
          Applications externes (Claude Desktop, claude.ai, agents tiers...) autorisées à
          interagir avec ce workspace via le protocole MCP. Chaque autorisation est limitée
          aux permissions accordées au moment de la connexion.
        </p>
      </header>

      <section className="card">
        <header className="flex items-baseline justify-between gap-3 border-b border-border pb-3 mb-4">
          <h2 className="text-lg font-semibold">Autorisations actives</h2>
          <span className="text-xs text-text-muted tabular-nums">
            {query.data?.length ?? 0}
          </span>
        </header>

        {query.isLoading && <p className="text-text-muted text-sm">Chargement…</p>}
        {query.isError && (
          <p className="field-error">Erreur : {getErrorMessage(query.error)}</p>
        )}
        {query.data && query.data.length === 0 && (
          <EmptyState />
        )}
        {query.data && query.data.length > 0 && (
          <ul className="divide-y divide-border">
            {query.data.map((authz) => (
              <AuthorizationRow
                key={authz.id}
                authz={authz}
                confirming={confirmRevoke === authz.id}
                onConfirm={() => setConfirmRevoke(authz.id)}
                onCancel={() => setConfirmRevoke(null)}
                onRevoke={() => revokeMutation.mutate(authz.id)}
                isRevoking={revokeMutation.isPending && confirmRevoke === authz.id}
              />
            ))}
          </ul>
        )}
        {revokeMutation.isError && (
          <p className="field-error mt-3">{getErrorMessage(revokeMutation.error)}</p>
        )}
      </section>

      <section className="card bg-surface-2 border-border">
        <h2 className="text-base font-semibold mb-2">Comment connecter une application ?</h2>
        <ol className="text-sm text-text-muted space-y-2 list-decimal pl-5">
          <li>
            Dans Claude (Desktop ou Web), ajoutez un connecteur MCP personnalisé pointant
            vers <code className="font-mono text-xs bg-surface px-1 rounded">
              {window.location.origin}/api/v1/mcp
            </code>.
          </li>
          <li>
            Claude redirigera votre navigateur vers cette plateforme pour l’autorisation OAuth.
          </li>
          <li>
            Choisissez les permissions à accorder. Vous pourrez les révoquer à tout moment depuis
            cette page.
          </li>
        </ol>
        <p className="text-xs text-text-subtle mt-3">
          Aucune donnée répondant (réponses individuelles) n’est exposée par défaut. La permission
          correspondante <em>responses:read</em> reste désactivée tant que l’AIPD enquête
          mise à jour n’a pas été signée (RGPD art. 35).
        </p>
      </section>
    </div>
  );
}

function AuthorizationRow({
  authz,
  confirming,
  onConfirm,
  onCancel,
  onRevoke,
  isRevoking,
}: {
  authz: McpAuthorization;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onRevoke: () => void;
  isRevoking: boolean;
}): JSX.Element {
  return (
    <li className="py-3 flex flex-wrap items-start justify-between gap-3">
      <div className="flex-1 min-w-[260px] space-y-2">
        <p className="font-medium text-text">{authz.client_name}</p>
        <div className="flex flex-wrap gap-1.5">
          {authz.scopes.map((s) => {
            const info = SCOPE_BADGE[s] ?? { label: s, tone: 'neutral' as const };
            const classes =
              info.tone === 'danger'
                ? 'bg-danger-soft text-danger-text ring-danger-border'
                : info.tone === 'warning'
                ? 'bg-warning-soft text-warning-text ring-warning-border'
                : 'bg-surface-2 text-text-muted ring-border';
            return (
              <span
                key={s}
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 ${classes}`}
              >
                {info.label}
              </span>
            );
          })}
        </div>
        <p className="text-[11px] text-text-subtle">
          Créée le {authz.created_at_day}
          {authz.last_used_at_day
            ? ` · dernière utilisation le ${authz.last_used_at_day}`
            : ' · jamais utilisée'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {!confirming ? (
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={onConfirm}
          >
            Révoquer
          </button>
        ) : (
          <>
            <button type="button" className="btn-ghost text-xs" onClick={onCancel} disabled={isRevoking}>
              Annuler
            </button>
            <button
              type="button"
              className="btn-danger text-xs"
              onClick={onRevoke}
              disabled={isRevoking}
            >
              {isRevoking ? 'Révocation…' : 'Confirmer la révocation'}
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-8 space-y-2">
      <p className="text-text-muted">Aucun connecteur IA autorisé pour le moment.</p>
      <p className="text-xs text-text-subtle">
        Ajoutez un connecteur MCP dans votre application Claude pour démarrer.
      </p>
    </div>
  );
}
